import { Box, Button, Grid, Paper, Typography, useTheme } from '@mui/material';
import { get as _get } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { useModalVisibility } from '../../../app/hooks/useModalVisibility';
import { setErsClaim, setGlassClaim } from '../../../app/store/ClaimCreation/ClaimCreationSlice';
import {
  getAuthKey,
  getAutoDrpSearchFlag,
  getAutoRentalShopSelection,
  getClaimNumber,
  getPartnerId,
  getServicingOpCo,
  getSessionId,
  getUrlKey,
  getUserData,
} from '../../../app/store/combinedSelectors';
import { resetAvailableProgressStagesState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { goToClaimsBtn } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { setupPostFnol } from '../../../services/SetupPostFnol.service';
import {
  ClaimActivityEntry,
  GenericProductType,
  OpCo,
  PartnerId,
  PaymentTransaction,
  ProviderInfo,
  StageNames,
} from '../../../types/type';
import { getVehiclesInfoText } from '../../../utils/Policy-Util';
import { formatDate, getPrettyClaimNumber, postMessageToParentApp } from '../../../utils/Utilities';
import Accordion from '../../shared/Accordion';
import AdjusterInfoCard from '../../shared/AdjusterInfoCard/AdjusterInfoCard';
import ClaimActivityCard from '../../shared/ClaimActivity/ClaimActivity';
import ClaimDescriptionLink from '../../shared/ClaimDescriptionLink/ClaimDescriptionLink';
import ClaimSummary from '../../shared/ClaimSummary/ClaimSummary';
import ServiceCompanyInfo from '../../shared/ServiceCompanyInfo/ServiceCompanyInfo';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';
import PhotoAccordion from '../Accordion/PhotoAccordion';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import ModalLinearActivityIndicator from '../ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import ClaimDetailNextSteps from './ClaimDetailNextSteps';
import ClaimDetailsPayments from './ClaimDetailPayments';
import ClaimDetailPhotos from './ClaimDetailPhotos';
import ClaimDetailsPLNAR from './ClaimDetailPLNAR';
import ClaimDetailRental from './ClaimDetailRental';
import ClaimDetailRepair from './ClaimDetailRepair';
import ClaimDetailSummary from './ClaimDetailSummary';

interface LoadTypeInfo {
  isClaimDetailRefreshNeeded: boolean | null;
}

function buildClaimActivityData(
  claimOpenedDate: string,
  claimClosedDate: string,
  paymentInfo: PaymentTransaction[] | undefined,
  serviceProviderInfo: ProviderInfo[] | undefined,
  lineOfBusiness: GenericProductType
): ClaimActivityEntry[] {
  const activities: ClaimActivityEntry[] = [];

  if (claimClosedDate) {
    activities.push({
      date: formatDate(claimClosedDate.split('T')[0]),
      message: 'Claim closed',
    });
  }

  if (paymentInfo && paymentInfo.length > 0) {
    paymentInfo.forEach((payment) => {
      activities.push({
        date: payment.TransactionDate,
        message: `${payment.Amount} payment to ${payment.PayeeName}`,
      });
    });
  }

  if (claimOpenedDate) {
    activities.push({
      date: formatDate(claimOpenedDate.split('T')[0]),
      message: 'Claim opened',
    });
  }

  const isAuto = lineOfBusiness === GenericProductType.Auto || lineOfBusiness === GenericProductType.CommercialAuto;

  if (isAuto && serviceProviderInfo && serviceProviderInfo.length > 0) {
    const repairProvider = serviceProviderInfo.find((p) => p.serviceTypeName === StageNames.REPAIR);
    if (repairProvider?.serviceRequestDate) {
      activities.push({
        date: formatDate(repairProvider.serviceRequestDate.split('T')[0]),
        message: 'Shop info received',
      });
    }

    const rentalProvider = serviceProviderInfo.find((p) => p.serviceTypeName === StageNames.RENTAL);
    if (rentalProvider?.serviceRequestDate) {
      activities.push({
        date: formatDate(rentalProvider.serviceRequestDate.split('T')[0]),
        message: 'Rental car info received',
      });
    }

    const towingProvider = serviceProviderInfo.find((p) => p.serviceTypeName === StageNames.TOWING);
    if (towingProvider?.serviceRequestDate) {
      activities.push({
        date: formatDate(towingProvider.serviceRequestDate.split('T')[0]),
        message: 'Towing info received',
      });
    }
  }

  return activities.filter((activity) => activity.date);
}

function ClaimDetailDetails(props: any) {
  const theme = useTheme();
  const [toggleOpenParam, setToggleOpenParam] = useState(false);
  const [toggleCloseParam, setToggleCloseParam] = useState(true);
  const [buttonFlag, setButtonFlag] = useState('expand');
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  // Calls the custom hook useModalVisibility, passing in the setModalLoadingOpen function.The hook returns a new function, setModalVisibility, which can be used to show or hide the modal.
  const setModalVisibility = useModalVisibility(setModalLoadingOpen);
  const [showErrorBanner, setShowErrorBanner] = React.useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { isClaimDetailRefreshNeeded } = (state as LoadTypeInfo) ?? false;
  const claimNumber = useSelector(getClaimNumber);
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const customerData = useSelector(getUserData);
  const opco = useSelector(getServicingOpCo);
  const partnerId = useSelector(getPartnerId);
  const autoDrpSearchFlag = useSelector(getAutoDrpSearchFlag);
  const autoRentalShopSelection = useSelector(getAutoRentalShopSelection);

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    /**
     * Reset glass and ERS claim flags when entering claim details
     * This ensures flags don't persist when viewing claim details
     */
    dispatch(setGlassClaim({ isGlassClaim: false }));
    dispatch(setErsClaim({ isErsClaim: false }));

    if (isClaimDetailRefreshNeeded) {
      getClaimDetail();
    }
    async function getClaimDetail() {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
      setModalVisibility(true);
      const successfulSetup = await setupPostFnol(
        customerData,
        claimNumber,
        urlKey,
        sessionId,
        authKey,
        true,
        opco,
        'claim-detail-page'
      );
      setModalVisibility(false);
      if (
        successfulSetup === GenericProductType.Auto ||
        successfulSetup === GenericProductType.Home ||
        successfulSetup === GenericProductType.CommercialAuto ||
        successfulSetup === GenericProductType.CommercialProperty ||
        successfulSetup === GenericProductType.WorkersComp
      ) {
        setShowErrorBanner(false);
      } else {
        postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_CLAIM_DETAIL_FAIL_ERROR, claimNumber, successfulSetup);
        setShowErrorBanner(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run useEffect only once here. Hence dependency array is empty

  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);
  const claimLineOfBusiness = _get(claimSummary, 'lineOfBusiness');
  const isCommercialClaim =
    claimLineOfBusiness === GenericProductType.CommercialAuto ||
    claimLineOfBusiness === GenericProductType.CommercialProperty;
  const isWorkersCompClaim = claimLineOfBusiness === GenericProductType.WorkersComp;

  // Get properly formatted status for display (reverse the past tense transformation from getClaimStatusText)
  const getDisplayStatus = (status: string): string => {
    if (status.toLowerCase() === 'opened') {
      return 'Open';
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const staticProgressStages = useAppSelector((state) => state.claimDetail.claimSummary?.claimProgressStates);
  const waterRemovalServiceProvider = useAppSelector((state) =>
    state.claimDetail.claimSummary?.serviceProviderInfo?.filter(
      (provider) => provider.serviceTypeName === StageNames.WATER_REMOVAL
    )
  );
  const emergencyServiceProvider = useAppSelector((state) =>
    state.claimDetail.claimSummary?.serviceProviderInfo?.filter(
      (provider) => provider.serviceTypeName === StageNames.EMERGENCY_SERVICES
    )
  );
  const documentDamageServiceProvider = useAppSelector((state) =>
    state.claimDetail.claimSummary?.serviceProviderInfo?.filter(
      (provider) => provider.serviceTypeName === StageNames.DOCUMENT_DAMAGE
    )
  );

  const repairServiceProvider = useAppSelector((state) =>
    state.claimDetail.claimSummary?.serviceProviderInfo?.filter(
      (provider) => provider.serviceTypeName === StageNames.REPAIR
    )
  );

  let subHeaderContent: string | JSX.Element = '';

  let claimSummaryInfo: Array<{ rowName: string; rowValue: string | JSX.Element }> = [];

  if (isWorkersCompClaim) {
    // Workers Comp claims: Don't show claimant name in top section, only in summary
    const firstName = _get(claimSummary, 'insured.firstName', '');
    const lastName = _get(claimSummary, 'insured.lastName', '');
    const claimantName = `${firstName} ${lastName}`.trim();

    subHeaderContent = ''; // Empty for Workers Comp - claimant name only shown in summary section

    claimSummaryInfo = [
      {
        rowName: 'Date Filed',
        rowValue: claimSummary?.claimOpenedDate ? formatDate(claimSummary.claimOpenedDate.split('T')[0]) : '',
      },
      {
        rowName: 'Date of Incident',
        rowValue: claimSummary?.lossDate || '',
      },
      {
        rowName: 'Claimant Name',
        rowValue: claimantName,
      },
      {
        rowName: 'Policy Number',
        rowValue: claimSummary.policyNumber,
      },
      {
        rowName: 'Incident Description',
        rowValue: _get(claimSummary, 'claimDescription', ''),
      },
    ];
  } else if (!claimSummary?.autoClaimDetails || claimSummary?.autoClaimDetails.length === 0) {
    subHeaderContent = _get(claimSummary, 'primaryAddressFirstLine', '');

    claimSummaryInfo = [
      {
        rowName: 'Date Filed',
        rowValue: _get(claimSummary, 'reportedDate', ''),
      },
      {
        rowName: 'Date and Time of Incident',
        rowValue: `${claimSummary?.lossDate + ' at ' + claimSummary?.timeOfIncident}`,
      },
      {
        rowName: 'Property',
        rowValue: _get(claimSummary, 'primaryAddressFirstLine', ''),
      },
      {
        rowName: 'Incident Description',
        rowValue: _get(claimSummary, 'claimDescription', ''),
      },
      {
        rowName: 'Type of Incident',
        rowValue: _get(claimSummary, 'lossCauseDescription', ''),
      },
    ];
  } else {
    // Plain text for the ClaimSummary header (no hyperlink, no vehicle count).
    subHeaderContent = getVehiclesInfoText(claimSummary, false);

    // Clickable link (with count) used only in the Vehicle detail row.
    const vehicleLink = (
      <ClaimDescriptionLink
        description={getVehiclesInfoText(claimSummary)}
        onMultipleVehiclesClick={() => navigate('/vehicles', { state: { claimNumber: claimSummary?.claimNumber } })}
      />
    );

    claimSummaryInfo = [
      {
        rowName: 'Date Filed',
        rowValue: claimSummary?.reportedDate,
      },
      {
        rowName: 'Date and Time of Incident',
        rowValue: `${claimSummary?.lossDate + ' at ' + claimSummary?.timeOfIncident}`,
      },
      {
        rowName: 'Vehicle',
        rowValue: vehicleLink,
      },
      {
        rowName: 'Policy Number',
        rowValue: claimSummary.policyNumber,
      },
      {
        rowName: 'Incident Description',
        rowValue: claimSummary?.claimDescription,
      },
      {
        rowName: 'Type of Incident',
        rowValue: claimSummary?.lossCauseDescription,
      },
      {
        rowName: 'Location',
        rowValue: claimSummary?.lossLocationAddress.city + ' ' + claimSummary?.lossLocationAddress.state,
      },
    ];
  }

  function toggleOpen() {
    setButtonFlag('collapse');
    setToggleOpenParam(true);
    setToggleCloseParam(false);
  }

  function toggleClose() {
    setToggleCloseParam(true);
    setToggleOpenParam(false);
    setButtonFlag('expand');
  }

  async function goToClaimsOverview() {
    // Reset availableProgressStages state
    dispatch(resetAvailableProgressStagesState());

    postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
    if (partnerId?.toUpperCase() === PartnerId.AFICS) {
      setModalLoadingOpen(true);
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_POST_FNOL_GO_TO_CLAIMS,
        undefined,
        undefined,
        claimSummary?.policyNumber
      );
    } else if (opco === OpCo.HOMESITE || opco === OpCo.AMFAM) {
      navigate('/claims-overview', {
        state: {
          isClaimRefreshNeeded: true,
        },
      });
    } else {
      setModalVisibility(true);
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_POST_FNOL_GO_TO_CLAIMS,
        undefined,
        undefined,
        claimSummary?.policyNumber
      );
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      {showErrorBanner && <ErrorBanner></ErrorBanner>}
      {claimSummary && (
        <>
          <Grid
            container
            sx={{
              width: '100%',
              maxWidth: '1280px',
              minWidth: '310px',
              margin: '0 auto',
            }}
          >
            <ClaimSummary
              headerContent={'Claim: ' + getPrettyClaimNumber(claimSummary.claimNumber)}
              subHeaderContent={subHeaderContent}
              maskSubHeaderInDatadog={isWorkersCompClaim}
            ></ClaimSummary>
          </Grid>
          {!isWorkersCompClaim && (
            <>
              <Grid
                sx={{
                  width: '100%',
                  maxWidth: '1280px',
                  minWidth: '310px',
                  margin: '0 auto',
                }}
              >
                {staticProgressStages && <StepProgressBar stateInfo={staticProgressStages}></StepProgressBar>}
              </Grid>
              <Grid sx={{ marginTop: '-2rem', marginBottom: '1rem' }}>
                <ClaimDetailNextSteps
                  claimProgressStateType={claimSummary.currentClaimProgressState}
                ></ClaimDetailNextSteps>
              </Grid>
            </>
          )}
          {isWorkersCompClaim && (
            <Grid
              container
              sx={{
                width: '100%',
                maxWidth: '1280px',
                minWidth: '310px',
                margin: '0 auto',
              }}
            >
              <Grid item xs={12}>
                <Typography
                  sx={{
                    ...theme.custom.styles.titleType3,
                    marginTop: '0.5rem',
                    marginBottom: '2rem',
                  }}
                >
                  {claimSummary.claimClosedDate
                    ? `${getDisplayStatus(claimSummary.claimStatusCode)} : ${formatDate(claimSummary.claimClosedDate.split('T')[0])}`
                    : getDisplayStatus(claimSummary.claimStatusCode)}
                </Typography>
              </Grid>
            </Grid>
          )}
          <Grid
            container
            sx={{
              width: '100%',
              maxWidth: '1280px',
              minWidth: '310px',
              margin: '0 auto',
            }}
          >
            {/*Hide this for Commercial claims and Workers Comp claims */}
            {!isCommercialClaim && !isWorkersCompClaim && (
              <ClaimActivityCard
                activities={buildClaimActivityData(
                  claimSummary.claimOpenedDate,
                  claimSummary.claimClosedDate,
                  claimSummary.paymentInfo,
                  claimSummary.serviceProviderInfo,
                  claimSummary.lineOfBusiness
                )}
              />
            )}
            <Grid container>
              <Grid
                sx={{
                  paddingTop: '2em',
                }}
                item
                xs={8}
                sm={8}
              >
                <Typography sx={theme.custom.styles.titleType2}>Claim Details</Typography>
              </Grid>

              <Grid
                sx={{
                  textAlign: 'right',
                  marginTop: 'auto',
                }}
                item
                xs={4}
                sm={4}
              >
                <Button
                  disableRipple
                  sx={
                    buttonFlag === 'expand'
                      ? theme.custom.styles.expandCollapseButton
                      : theme.custom.styles.expandCollapseDisabledButton
                  }
                  onClick={toggleOpen}
                >
                  Expand All
                </Button>
                /
                <Button
                  disableRipple
                  sx={
                    buttonFlag === 'collapse'
                      ? theme.custom.styles.expandCollapseButton
                      : theme.custom.styles.expandCollapseDisabledButton
                  }
                  onClick={toggleClose}
                >
                  Collapse All
                </Button>
              </Grid>
              <Grid item xs={12}>
                {theme.custom.styles.showUnderlinedAccordionRowHeader && (
                  <hr
                    style={{
                      border: theme.custom.styles.hrStyle,
                      width: '100%',
                    }}
                  ></hr>
                )}
              </Grid>

              {/* Claim Summary Accordion */}
              {!!claimSummaryInfo && (
                <Grid item xs={12} sx={theme.custom.styles.accordionRowGridItemStyle}>
                  <Paper
                    variant={theme.custom.styles.accordionRowHeader.variant as any} // Why 'any'? For more, refer => https://github.com/mui/material-ui/issues/15697
                    elevation={theme.custom.styles.accordionRowHeader.elevation}
                    sx={{
                      p: 0.1,
                      borderRadius: theme.custom.styles.borderRadius.radiusDefault,
                      borderColor: theme.custom.styles.showBorderAroundListItems
                        ? theme.custom.colors.defaultBorderColor
                        : theme.custom.colors.bodyColor,
                    }}
                  >
                    <Accordion
                      title="Summary"
                      toggleOpen={toggleOpenParam}
                      toggleClose={toggleCloseParam}
                      needsGradient={true}
                      component={<ClaimDetailSummary info={claimSummaryInfo}></ClaimDetailSummary>}
                    />
                  </Paper>
                </Grid>
              )}

              {/* Emergency Services Accordion */}
              {!isCommercialClaim &&
                !isWorkersCompClaim &&
                emergencyServiceProvider &&
                emergencyServiceProvider?.length > 0 && (
                  <Grid item xs={12} sx={theme.custom.styles.accordionRowGridItemStyle}>
                    {theme.custom.styles.showUnderlinedAccordionRowHeader && (
                      <hr
                        style={{
                          border: theme.custom.styles.hrStyle,
                          width: '100%',
                        }}
                      ></hr>
                    )}
                    <Paper
                      variant={theme.custom.styles.accordionRowHeader.variant as any} // Why 'any'? For more, refer => https://github.com/mui/material-ui/issues/15697
                      elevation={theme.custom.styles.accordionRowHeader.elevation}
                      sx={{
                        p: 0.1,
                        borderRadius: theme.custom.styles.borderRadius.radiusDefault,
                        borderColor: theme.custom.styles.showBorderAroundListItems
                          ? theme.custom.colors.defaultBorderColor
                          : theme.custom.colors.bodyColor,
                      }}
                    >
                      {emergencyServiceProvider[0].serviceCreationStatus && (
                        <Accordion
                          title="Emergency Services"
                          toggleOpen={toggleOpenParam}
                          toggleClose={toggleCloseParam}
                          component={
                            <ServiceCompanyInfo
                              isRadioButtonEnabled={false}
                              provider={emergencyServiceProvider[0]}
                            ></ServiceCompanyInfo>
                          }
                        />
                      )}
                      {!emergencyServiceProvider[0].serviceCreationStatus && (
                        <Accordion
                          title="Emergency Services"
                          toggleOpen={toggleOpenParam}
                          toggleClose={toggleCloseParam}
                          component={
                            <Typography
                              variant="body1"
                              sx={{
                                marginTop: '1rem',
                                marginLeft: '1rem',
                                marginBottom: '1rem',
                              }}
                            >
                              If you have not been contacted by the Emergency Services partner within 1 hour, please
                              contact us.
                            </Typography>
                          }
                        />
                      )}
                    </Paper>
                  </Grid>
                )}

              {/* Water Removal Accordion */}
              {!isCommercialClaim &&
                !isWorkersCompClaim &&
                waterRemovalServiceProvider &&
                waterRemovalServiceProvider.length > 0 && (
                  <Grid item xs={12} sx={theme.custom.styles.accordionRowGridItemStyle}>
                    {theme.custom.styles.showUnderlinedAccordionRowHeader && (
                      <hr
                        style={{
                          border: theme.custom.styles.hrStyle,
                          width: '100%',
                        }}
                      ></hr>
                    )}
                    <Paper
                      variant={theme.custom.styles.accordionRowHeader.variant as any} // Why 'any'? For more, refer => https://github.com/mui/material-ui/issues/15697
                      elevation={theme.custom.styles.accordionRowHeader.elevation}
                      sx={{
                        p: 0.1,
                        borderRadius: theme.custom.styles.borderRadius.radiusDefault,
                        borderColor: theme.custom.styles.showBorderAroundListItems
                          ? theme.custom.colors.defaultBorderColor
                          : theme.custom.colors.bodyColor,
                      }}
                    >
                      {waterRemovalServiceProvider[0].serviceCreationStatus && (
                        <Accordion
                          title="Water Removal"
                          toggleOpen={toggleOpenParam}
                          toggleClose={toggleCloseParam}
                          component={
                            <ServiceCompanyInfo
                              isRadioButtonEnabled={false}
                              provider={waterRemovalServiceProvider[0]}
                            ></ServiceCompanyInfo>
                          }
                        />
                      )}
                      {!waterRemovalServiceProvider[0].serviceCreationStatus && (
                        <Accordion
                          title="Water Removal"
                          toggleOpen={toggleOpenParam}
                          toggleClose={toggleCloseParam}
                          component={
                            <Typography
                              variant="body1"
                              sx={{
                                marginTop: '1rem',
                                marginLeft: '1rem',
                                marginBottom: '1rem',
                              }}
                            >
                              If you have not been contacted by the Emergency Water Removal partner within 1 hour,
                              please contact us.
                            </Typography>
                          }
                        />
                      )}
                    </Paper>
                  </Grid>
                )}

              {/* Document Damages / PLNAR Accordion */}
              {!isCommercialClaim &&
                !isWorkersCompClaim &&
                documentDamageServiceProvider &&
                documentDamageServiceProvider.length > 0 && (
                  <Grid item xs={12} sx={theme.custom.styles.accordionRowGridItemStyle}>
                    {theme.custom.styles.showUnderlinedAccordionRowHeader && (
                      <hr
                        style={{
                          border: theme.custom.styles.hrStyle,
                          width: '100%',
                        }}
                      ></hr>
                    )}
                    <Paper
                      variant={theme.custom.styles.accordionRowHeader.variant as any} // Why 'any'? For more, refer => https://github.com/mui/material-ui/issues/15697
                      elevation={theme.custom.styles.accordionRowHeader.elevation}
                      sx={{
                        p: 0.1,
                        borderRadius: theme.custom.styles.borderRadius.radiusDefault,
                        borderColor: theme.custom.styles.showBorderAroundListItems
                          ? theme.custom.colors.defaultBorderColor
                          : theme.custom.colors.bodyColor,
                      }}
                    >
                      <Accordion
                        title="Document Damages"
                        toggleOpen={toggleOpenParam}
                        toggleClose={toggleCloseParam}
                        component={<ClaimDetailsPLNAR></ClaimDetailsPLNAR>}
                      />
                    </Paper>
                  </Grid>
                )}

              {/* Photos and Rental Accordion */}
              {!isCommercialClaim &&
                !isWorkersCompClaim &&
                !!claimSummary?.autoClaimDetails &&
                claimSummary?.autoClaimDetails.length > 0 && (
                  <>
                    <Grid item xs={12} sx={theme.custom.styles.accordionRowGridItemStyle}>
                      {theme.custom.styles.showUnderlinedAccordionRowHeader && (
                        <hr
                          style={{
                            border: theme.custom.styles.hrStyle,
                            width: '100%',
                          }}
                        ></hr>
                      )}
                      <Paper
                        variant={theme.custom.styles.accordionRowHeader.variant as any} // Why 'any'? For more, refer => https://github.com/mui/material-ui/issues/15697
                        elevation={theme.custom.styles.accordionRowHeader.elevation}
                        sx={{
                          p: 0.1,
                          borderRadius: theme.custom.styles.borderRadius.radiusDefault,
                          borderColor: theme.custom.styles.showBorderAroundListItems
                            ? theme.custom.colors.defaultBorderColor
                            : theme.custom.colors.bodyColor,
                        }}
                      >
                        <PhotoAccordion
                          expanded={toggleOpenParam}
                          title="Photos"
                          component={<ClaimDetailPhotos setModalLoadingOpen={setModalLoadingOpen}></ClaimDetailPhotos>}
                        ></PhotoAccordion>
                      </Paper>
                    </Grid>
                    {autoRentalShopSelection && (
                      <Grid item xs={12} sx={theme.custom.styles.accordionRowGridItemStyle}>
                        {theme.custom.styles.showUnderlinedAccordionRowHeader && (
                          <hr
                            style={{
                              border: theme.custom.styles.hrStyle,
                              width: '100%',
                            }}
                          ></hr>
                        )}
                        <Paper
                          variant={theme.custom.styles.accordionRowHeader.variant as any} // Why 'any'? For more, refer => https://github.com/mui/material-ui/issues/15697
                          elevation={theme.custom.styles.accordionRowHeader.elevation}
                          sx={{
                            p: 0.1,
                            borderRadius: theme.custom.styles.borderRadius.radiusDefault,
                            borderColor: theme.custom.styles.showBorderAroundListItems
                              ? theme.custom.colors.defaultBorderColor
                              : theme.custom.colors.bodyColor,
                          }}
                        >
                          <Accordion
                            title="Rental Vehicle"
                            toggleOpen={toggleOpenParam}
                            toggleClose={toggleCloseParam}
                            component={<ClaimDetailRental />}
                          />
                        </Paper>
                      </Grid>
                    )}
                  </>
                )}

              {/* Payments Accordion - Hidden for Workers Comp claims */}
              {!isWorkersCompClaim && (
                <Grid item xs={12} sx={theme.custom.styles.accordionRowGridItemStyle}>
                  {theme.custom.styles.showUnderlinedAccordionRowHeader && (
                    <hr
                      style={{
                        border: theme.custom.styles.hrStyle,
                        width: '100%',
                      }}
                    ></hr>
                  )}
                  <Paper
                    variant={theme.custom.styles.accordionRowHeader.variant as any} // Why 'any'? For more, refer => https://github.com/mui/material-ui/issues/15697
                    elevation={theme.custom.styles.accordionRowHeader.elevation}
                    sx={{
                      p: 0.1,
                      borderRadius: theme.custom.styles.borderRadius.radiusDefault,
                      borderColor: theme.custom.styles.showBorderAroundListItems
                        ? theme.custom.colors.defaultBorderColor
                        : theme.custom.colors.bodyColor,
                    }}
                  >
                    <Accordion
                      title="Payments"
                      toggleOpen={toggleOpenParam}
                      toggleClose={toggleCloseParam}
                      component={<ClaimDetailsPayments paymentInfo={claimSummary?.paymentInfo}></ClaimDetailsPayments>}
                    />
                  </Paper>
                </Grid>
              )}

              {/* DRP Accordion */}
              {!isCommercialClaim &&
                !isWorkersCompClaim &&
                !!claimSummary?.autoClaimDetails &&
                claimSummary?.autoClaimDetails.length > 0 &&
                !!repairServiceProvider &&
                repairServiceProvider.length > 0 &&
                autoDrpSearchFlag && (
                  <Grid item xs={12} sx={theme.custom.styles.accordionRowGridItemStyle}>
                    {theme.custom.styles.showUnderlinedAccordionRowHeader && (
                      <hr
                        style={{
                          border: theme.custom.styles.hrStyle,
                          width: '100%',
                        }}
                      ></hr>
                    )}
                    <Paper
                      variant={theme.custom.styles.accordionRowHeader.variant as any} // Why 'any'? For more, refer => https://github.com/mui/material-ui/issues/15697
                      elevation={theme.custom.styles.accordionRowHeader.elevation}
                      sx={{
                        p: 0.1,
                        borderRadius: theme.custom.styles.borderRadius.radiusDefault,
                        borderColor: theme.custom.styles.showBorderAroundListItems
                          ? theme.custom.colors.defaultBorderColor
                          : theme.custom.colors.bodyColor,
                      }}
                    >
                      <Accordion
                        title="Towing and Repair Shops"
                        toggleOpen={toggleOpenParam}
                        toggleClose={toggleCloseParam}
                        component={<ClaimDetailRepair serviceRequestData={repairServiceProvider}></ClaimDetailRepair>}
                      />
                    </Paper>
                  </Grid>
                )}

              <Grid item xs={12}>
                <AdjusterInfoCard
                  headerText={'Claim Contact'}
                  adjusterInfo={claimSummary?.adjusterInfo}
                ></AdjusterInfoCard>
              </Grid>
              <Grid item xs={12} sx={theme.custom.styles.goToClaimButtonGridStyle}>
                <Button
                  sx={{
                    ...theme.custom.styles.genericTertiaryButton,
                  }}
                  onClick={goToClaimsOverview}
                >
                  {goToClaimsBtn}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
    </Box>
  );
}

export default ClaimDetailDetails;
